import { formatNamedDate, startOfLocalDay } from '../lib/date/localDay';

import formatCalendarDay from './formatCalendarDay';

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/**
 * Compact relative timestamp for alert list rows, in both directions — alerts
 * carry past event dates, while reminders can sit days in the future.
 *
 * | when                      | example                    |
 * | ------------------------- | -------------------------- |
 * | within a minute           | `Just now`                 |
 * | under an hour             | `20 min ago` / `In 20 min` |
 * | same local day            | `2 hr ago` / `In 2 hr`     |
 * | adjacent calendar day     | `Yesterday` / `Tomorrow`   |
 * | 2–6 days ago              | `Tuesday`                  |
 * | 7+ days ago, or 2+ ahead  | `Jun 2`                    |
 * | different year            | `Jun 2, 2024`              |
 *
 * Sub-hour diffs use a rolling window (a 40-minute-old alert reads
 * `40 min ago` even across midnight); larger diffs bucket on local-midnight
 * boundaries so day labels stay stable across re-renders within a day.
 * `now` is injectable for deterministic tests.
 */
export default function formatCompactTimeAgo(date: Date, now: Date = new Date()): string {
  const diffMs = now.getTime() - date.getTime();
  const absMs = Math.abs(diffMs);
  const isPast = diffMs > 0;

  if (absMs < MS_PER_MINUTE) {
    return 'Just now';
  }
  if (absMs < MS_PER_HOUR) {
    const minutes = Math.floor(absMs / MS_PER_MINUTE);
    return isPast ? `${minutes} min ago` : `In ${minutes} min`;
  }

  const dayDiff = Math.round((startOfLocalDay(now) - startOfLocalDay(date)) / MS_PER_DAY);
  if (dayDiff === 0) {
    const hours = Math.floor(absMs / MS_PER_HOUR);
    return isPast ? `${hours} hr ago` : `In ${hours} hr`;
  }
  if (dayDiff > 0) {
    return formatCalendarDay(date, now);
  }
  if (dayDiff === -1) {
    return 'Tomorrow';
  }
  return formatNamedDate(date, now);
}
